import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import {
  Attachment,
  CookiePreference,
  CurrentState,
  DigitalContract,
  DigitalContractProperty,
  DocuSignEvent,
  SignatureType,
  User
} from '@ui/shared/models';

import {
  ActionState,
  AddressPipe,
  AttachmentItemComponent,
  AttachmentsListComponent,
  ButtonComponent,
  CookieBannerComponent,
  DigitalContractService,
  getCookiesPreference,
  INFRASTRUCTURE_CONFIG,
  InitCookiesPreference,
  LocalStorageService,
  OpenCookieSettingsModal,
  SetCookiesPreference,
  WINDOW_REF
} from '@ui/legacy-lib';

import * as fromContractState from 'landlord/main/+state/digital-contract';
import { storageKeys } from '@ui/landlord/shared/config';
import { AsyncPipe, CurrencyPipe, DecimalPipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { SvgIconComponent } from 'angular-svg-icon';

@UntilDestroy()
@Component({
  selector: 'app-digital-contract-landing',
  templateUrl: './digital-contract-landing.component.html',
  styleUrls: ['./digital-contract-landing.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CookieBannerComponent,
    AsyncPipe,
    AttachmentsListComponent,
    TranslateModule,
    AttachmentItemComponent,
    SvgIconComponent,
    CurrencyPipe,
    AddressPipe,
    DecimalPipe,
    ButtonComponent
  ]
})
export class DigitalContractLandingComponent implements OnInit {
  private store = inject(Store);
  private route = inject(ActivatedRoute);
  private localStorageService = inject(LocalStorageService);
  private dmvService = inject(DigitalContractService);
  private windowRef = inject(WINDOW_REF);
  private config = inject(INFRASTRUCTURE_CONFIG);

  public processing$: Observable<boolean>;
  public qesActionState$: Observable<ActionState>;
  public qesLink$: Observable<string>;
  public cookiesPreference$: Observable<CookiePreference>;
  public currentStatus$: Observable<CurrentState>;

  public contract: DigitalContract;
  public actionRequired: boolean;
  public property: DigitalContractProperty;
  public token: string;
  public redirectUrl: string;
  public backFromDocuSign = false;
  public user: User;

  public get isActionRequired() {
    return (
      this.contract &&
      this.actionRequired &&
      !this.contract.signedDocumentCombinedFile &&
      !this.backFromDocuSign
    );
  }

  public get signatureRequired() {
    return (
      this.contract &&
      !this.actionRequired &&
      !this.contract.signedDocumentCombinedFile &&
      !this.backFromDocuSign
    );
  }

  public get getTotalRent() {
    return (
      this.property.rent +
        this.property.heatingCost +
        Number(this.property.additionalCosts) || 0
    );
  }

  public get image() {
    return this.property?.titleImage?.url;
  }

  public get isEsDownload() {
    return (
      (this.contract?.signatureType === SignatureType.ES_MAIL ||
        this.contract?.signatureType === SignatureType.ES_OFFICE) &&
      this.contract?.signedDocumentSingleFiles
    );
  }

  public get isAesDownload() {
    return (
      (this.contract?.signatureType === SignatureType.AES_MAIL ||
        this.contract?.signatureType === SignatureType.AES_OFFICE ||
        this.contract?.signatureType === SignatureType.AES_SMS) &&
      this.contract?.signedDocumentSingleFiles
    );
  }

  public get isQesDownload() {
    return (
      (this.contract?.signatureType === SignatureType.QES ||
        this.contract?.signatureType === SignatureType.QES_DEPRECATED) &&
      this.contract?.signedDocumentSingleFiles
    );
  }

  public get isQES() {
    return this.contract?.signatureType === SignatureType.QES_DEPRECATED;
  }

  ngOnInit() {
    const { token, event } = this.route.snapshot.queryParams;

    this.token = token;
    this.redirectUrl =
      this.config.environment.landlord_app_url + '/de/signContract';

    /*
     * Case when we get redirected from DocuSign back to our app
     */
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    if (event?.toUpperCase() === DocuSignEvent.SIGNING_COMPLETE) {
      this.localStorageService.removeItem(storageKeys.digitalContract);
      this.backFromDocuSign = true;
    }

    if (this.token) {
      this.currentStatus$ = this.store.select(
        fromContractState.getCurrentStatus
      );
      this.store.dispatch(new fromContractState.GetContractByToken(this.token));
      this.store.dispatch(new fromContractState.GetCurrentState(this.token));
    }

    this.processing$ = combineLatest([
      this.store.select(fromContractState.getSignDigitalContractLoading),
      this.store.select(fromContractState.getSignRedirectLoading),
      this.store
        .select(fromContractState.getCurrentStatusActionState)
        .pipe(map(state => state.pending))
    ]).pipe(map(array => array.some(a => a)));

    this.cookiesPreference$ = this.store.select(getCookiesPreference);

    this.qesActionState$ = this.store.select(
      fromContractState.getQesActionState
    );

    this.qesLink$ = this.store.select(fromContractState.getQesLink);

    this.store
      .select(fromContractState.getSignDigitalContractsData)
      .pipe(
        filter(contract => !!contract),
        untilDestroyed(this)
      )
      .subscribe((contract: DigitalContract) => {
        this.property = contract.property;
        this.contract = {
          ...contract,
          token: this.token
        };
        this.actionRequired = contract.actionRequired;

        this.user = this.dmvService.getUser(this.contract.landlordSigners[0]);
        // initially load the preference from LS and enable tracking if configured
        this.store.dispatch(new InitCookiesPreference());
      });
  }

  public downloadContract(attachment: Attachment) {
    this.store.dispatch(
      new fromContractState.DownloadContract(this.token, attachment.identifier)
    );
  }

  public downloadSingleFileContract(attachment: Attachment) {
    this.store.dispatch(
      new fromContractState.DownloadContract(this.token, attachment.identifier)
    );
  }

  public downloadArchiveContract(attachment: Attachment) {
    this.store.dispatch(
      new fromContractState.DownloadContract(this.token, attachment.identifier)
    );
  }

  public sign() {
    this.localStorageService.setItem(
      storageKeys.digitalContract,
      this.contract
    );

    this.store.dispatch(
      new fromContractState.GetSigningUrl(this.token, this.redirectUrl)
    );
    this.store
      .select(fromContractState.getSignRedirect)
      .pipe(
        filter(docuSignRedirect => !!docuSignRedirect),
        untilDestroyed(this)
      )
      .subscribe(docuSignRedirect => {
        this.windowRef.open(docuSignRedirect.embeddedUrl, '_self');
      });
  }

  public acceptCookies(payload: CookiePreference) {
    this.store.dispatch(new SetCookiesPreference(payload));
  }

  public customCookieSettings(payload: CookiePreference) {
    this.store.dispatch(new OpenCookieSettingsModal(payload, false));
  }
}
